import {cn} from '@utils';
import {PropsWithChildren, useState, useEffect, useRef, useMemo} from 'react';

type Props = {
  numberOfMembers: number;
  numberOfOfficeDaysOnDay: number;
  strokeWidth?: number;
  className?: string;
  style?: React.CSSProperties;
};

export const DonutChart = ({
  numberOfMembers,
  numberOfOfficeDaysOnDay,
  strokeWidth = 4,
  children,
  className,
  style,
}: PropsWithChildren<Props>) => {
  const percentage = numberOfMembers > 0 ? (numberOfOfficeDaysOnDay / numberOfMembers) * 100 : 0;
  const clampedPercentage = Math.max(0, Math.min(100, percentage));

  const [size, setSize] = useState(20); // size based on children
  const half = size / 2;
  const radius = half - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;

  const pct = numberOfMembers === 0 ? 0 : numberOfOfficeDaysOnDay / numberOfMembers;

  const animatedProps = useMemo(
    () => ({
      strokeDashoffset: circumference * (1 - pct),
    }),
    [circumference, pct],
  );

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const {width, height} = contentRef.current.getBoundingClientRect();
      setSize(Math.max(width, height) + strokeWidth * 2);
    }
  }, [strokeWidth, children]);

  return (
    <div
      className={cn('relative flex justify-center items-center rounded-full', className)}
      style={style}>
      <svg
        width={size}
        height={size}>
        {/* Background Track */}
        <circle
          cx={half}
          cy={half}
          r={radius}
          stroke="blue"
          className="stroke-mapiq-black-800*/[0.03]"
          strokeWidth={strokeWidth}
          fill="none"
        />

        {/* Animated Progress */}
        <circle
          cx={half}
          cy={half}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          className="stroke-collaborative-blue-500* duration-500"
          style={{
            transitionProperty: 'stroke-dashoffset, stroke-dasharray',
          }}
          strokeDasharray={circumference}
          strokeDashoffset={animatedProps.strokeDashoffset}
          transform={`rotate(-90 ${half} ${half})`}
        />
      </svg>

      <div
        className="absolute flex items-center justify-center"
        ref={contentRef}>
        {children}
      </div>
    </div>
  );

  return (
    <div className="relative w-[150px] h-[150px] rounded-full overflow-hidden">
      {/* background track */}
      <div
        className="absolute inset-0"
        style={{
          background: `conic-gradient(#235bea ${clampedPercentage}%, #f2f2f2 ${clampedPercentage}%)`,
        }}
      />

      {/* white center */}
      <div className="absolute left-[10px] top-[10px] w-[130px] h-[130px] rounded-full bg-white grid place-items-center text-[32px] font-semibold">
        {Math.round(percentage)}
      </div>
    </div>
  );
};
